/**
 * Tool confirmation: rewrite an email step template.
 *
 * Input shape: {
 *   campaignId: string,
 *   stepId: string,
 *   stepIndex?: number,
 *   proposedSubject: string,
 *   proposedContent: string,  // with {{static}} + {ai-instruction} inline
 *   reason?: string
 * }
 *
 * Shows the proposed rewrite in-editable textareas. User approves or edits
 * further before saving. Syntax highlighting is a nice-to-have; keeping
 * this minimal for MVP.
 */

import { useState } from 'react';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import type { ToolConfirmationProps } from '../tool-registry';

export function RewriteEmailStepTool({ input, onConfirm, onReject }: ToolConfirmationProps) {
  const stepIndex = typeof input.stepIndex === 'number' ? input.stepIndex : undefined;
  const [subject, setSubject] = useState((input.proposedSubject as string) ?? '');
  const [content, setContent] = useState((input.proposedContent as string) ?? '');
  const reason = input.reason as string | undefined;

  return (
    <div className="h-full flex items-center justify-center p-8">
      <div className="bg-card border border-border rounded-2xl p-6 max-w-xl w-full shadow-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-tint-purple-surface flex items-center justify-center">
            <PencilSquareIcon className="h-5 w-5 text-category-purple" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Rewrite email step</h2>
            {stepIndex != null && (
              <p className="text-xs text-muted-foreground">Step {stepIndex + 1}</p>
            )}
          </div>
        </div>

        {reason && (
          <div className="bg-surface-sunken border border-border rounded-lg p-3 mb-4 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Agent's reason:</span> {reason}
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full text-sm px-3 py-2 rounded-lg border border-control-border bg-input-bg"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">
              Body (supports {'{{static}}'} + {'{ai-instruction}'})
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={12}
              className="w-full font-mono text-xs p-3 rounded-lg border border-control-border bg-input-bg"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="secondary" onClick={onReject}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => onConfirm({ ...input, proposedSubject: subject, proposedContent: content })}
          >
            Save rewrite
          </Button>
        </div>
      </div>
    </div>
  );
}
